const swea = require('swea');
import * as globals from '../globals';

const ModelManager = swea.editor.ModelManager;
const ViewerManager = swea.editor.ViewerManager;
const ContextMenuStateServiceActions = swea.react.actions.ContextMenuStateServiceActions;
const DialogServiceActions = swea.react.actions.DialogServiceActions;
const EditorStateServiceActions = swea.react.actions.EditorStateServiceActions;
const SelectInteraction = swea.editor.interactions.SelectInteraction;
const InstructionServiceActions = swea.react.actions.InstructionServiceActions;
const DialogService = swea.react.services.DialogService;
const AnnotationModel = swea.scene.models.AnnotationModel;
const PrimitiveModel = swea.scene.models.PrimitiveModel;
const LoadedModel = swea.scene.models.LoadedModel;
const PolygonModel = swea.scene.models.PolygonModel;
const PolygonBaseModel = swea.scene.models.PolygonBaseModel;
const VolumeModel = swea.scene.models.VolumeModel;
const SpriteModel = swea.scene.models.SpriteModel;
const ClassificationModel = swea.scene.models.ClassificationModel;
const Handle = swea.scene.models.Handle;
const LineAndSpriteModel = swea.scene.models.LineAndSpriteModel;
const MeasureService = swea.react.services.MeasureService;
const MergeLineAndSpriteModelsCommand = swea.editor.primitivemodelcommands.MergeLineAndSpriteModelsCommand;
const CornerSpriteModel = swea.scene.models.CornerSpriteModel;
const DistributedModel = swea.scene.models.DistributedModel;
const DepthProfileCylinderModel = swea.scene.models.DepthProfileCylinderModel;
const AnnotationServiceActions = swea.react.actions.AnnotationServiceActions;
const Polygon = swea.scene.primitives.Polygon;
const Line = swea.scene.primitives.Line;
const traversalMasks = swea.scene.nodes.traversalMasks;

/**
 * Creates a selection interaction that shows different context menus depending on selection.
 * @class
 */
class CustomSelectInteraction extends SelectInteraction {
  /**
   * The constructor.
   * @param { object } options An option object described below, see {@link SelectInteraction} for more info.
   * @param { boolean } options.onlyAnnotations True if only annotations should be selectable.
   * @param { boolean } options.extrude True if solid areas shall have an extrude handle.
   */
  constructor(options) {
    super(options);
    this._onlyAnnotations = options.onlyAnnotations;
    this._extrude = options.extrude;
  }

  /**
   * Initializes the interaction.
   * @override
   * @param { Element } element The DOM element receiving the interaction events.
   * @param { number } viewerId The ID of the viewer using this interaction.
   * @public
   * @override
   */
  initialize(element, viewer) {
    super.initialize(element, viewer);

    // Abort the interaction on escape.
    this.addTrigger(new swea.editor.interactions.EventHandler(
      [swea.editor.interactions.eventCodes.ESC],
      this,
      () => {
        this.deselect();
      })
    );

    // Shift+Right-click handler for merging line models.
    // When a line model is selected, Shift+Right-click on another line model to merge.
    this._mergeRightClickHandler = (evt) => {
      if (!evt.shiftKey) {
        return;
      }

      // Prevent the browser context menu
      evt.preventDefault();

      // Check currently selected model
      const currentSelectedId = ModelManager.instance().getSelectedModelId();
      const currentModel = currentSelectedId > -1 ? ModelManager.instance().getModel(currentSelectedId) : undefined;

      if (!(currentModel instanceof LineAndSpriteModel && currentModel.usesMeasuring())) {
        return;
      }

      // Pick what was right-clicked
      const sceneViewer = this.getSceneViewer();
      if (sceneViewer === undefined) {
        return;
      }

      const hit = sceneViewer.pick(evt.clientX, evt.clientY, {
        findTriangleCenter: false,
        pickSelectable: traversalMasks.ON,
      });

      if (hit === undefined) {
        return;
      }

      const clickedModel = hit.getModel();
      if (clickedModel === undefined || clickedModel === null || !clickedModel.isEnabled()) {
        return;
      }

      if (clickedModel.getId() === currentSelectedId) {
        return;
      }

      if (!(clickedModel instanceof LineAndSpriteModel && clickedModel.usesMeasuring())) {
        return;
      }

      this._attemptMerge(currentModel, clickedModel);
    };
    element.addEventListener('contextmenu', this._mergeRightClickHandler, false);
  }

  /**
   * Show context menu on selection.
   * @param { scene.models.Model } model The model that is unselected.
   * @param { number } viewerId (optional) The viewer ID.
   * @public
   * @override
   */
  whenSelected(model, viewerId) {
    if (this._onlyAnnotations && !(model instanceof AnnotationModel)) {
      ModelManager.instance().selectModel(-1);
      return;
    }
    super.whenSelected(model, viewerId);

    // Close context menu first, if this is not a bulldoze area or an instantly selectable primitive model
    let closeContextMenu = true;
    if (model instanceof PrimitiveModel) {
      if (model.isInstantlySelectable()) {
        closeContextMenu = false;
      }
      else {
        if (model instanceof PolygonModel && model.flattens()) {
          closeContextMenu = false;
        }
      }
    }
    if (closeContextMenu) {
      ContextMenuStateServiceActions.closeContextMenu(globals.LOWER_CONTEXT_MENU);
    }

    // Open new context menu depending on model type
    if (model instanceof PrimitiveModel) {
      if (model instanceof AnnotationModel && model.activatesMenu()) {
        if (closeContextMenu) {
          ContextMenuStateServiceActions.openContextMenu(
            globals.LOWER_CONTEXT_MENU,
            globals.SELECTED_ANNOTATION
          );
        }
      }
      else if (model._options.measure) {
        if (closeContextMenu) {
          ContextMenuStateServiceActions.openContextMenu(
            globals.LOWER_CONTEXT_MENU,
            globals.MEASURE_ACTIVATED
          );
        }
        if (!(model instanceof PolygonBaseModel && model.getMeasurementRefined())) {
          InstructionServiceActions.showInstruction('instructions.corner_interaction');
        }
      }
      else if (model instanceof LineAndSpriteModel) {
        if (closeContextMenu) {
          ContextMenuStateServiceActions.openContextMenu(
            globals.LOWER_CONTEXT_MENU,
            globals.SELECTED_TEXT_LINE
          );
        }
      }
      else if (model instanceof PolygonModel && !model.flattens() && !model._options.measure) {
        if (model.getHandle(Handle.Type.TOP) !== undefined) {
          // An extrudable solid area with top handle - show solid area instruction and context menu
          InstructionServiceActions.showInstruction(this._extrude ? 'instructions.solid_area_interaction_extrude' : 'instructions.solid_area_interaction');
          if (closeContextMenu) {
            ContextMenuStateServiceActions.openContextMenu(
              globals.LOWER_CONTEXT_MENU,
              globals.SELECTED_AREA
            );
          }
        }
        else {
          // A text area without top handle, show text area instruction and context menu
          InstructionServiceActions.showInstruction('instructions.text_area_interaction');
          if (closeContextMenu) {
            ContextMenuStateServiceActions.openContextMenu(
              globals.LOWER_CONTEXT_MENU,
              globals.SELECTED_TEXT_AREA
            );
          }
        }
      }
      else if (model instanceof VolumeModel) {
        if (closeContextMenu) {
          ContextMenuStateServiceActions.openContextMenu(
            globals.LOWER_CONTEXT_MENU,
            model.isSection() ? globals.SELECTED_SECTION_VOLUME : globals.SELECTED_VOLUME
          );
        }
      }
      else if (model instanceof DepthProfileCylinderModel) {
        // Pretend that the annotation model connected to the depth profile cylinder model is selected,
        // so that it's expanded
        const annotationModelId = model.getAnnotationModelId();
        AnnotationServiceActions.selectAnnotation(annotationModelId, viewerId);
        const annotationModel = ModelManager.instance().getModel(annotationModelId);
        if (annotationModel !== undefined) {
          annotationModel.setSelectedAppearance();
        }

        // Also show the annotation model context menu
        if (closeContextMenu) {
          ContextMenuStateServiceActions.openContextMenu(
            globals.LOWER_CONTEXT_MENU,
            globals.SELECTED_ANNOTATION
          );
        }
      }

      if (!MeasureService.instance().isRefining()) {
        const primitive = model.getPrimitive(0);
        if (model instanceof CornerSpriteModel || primitive instanceof Polygon || primitive instanceof Line) {
          if (!(model instanceof PolygonBaseModel && model.getMeasurementRefined())) {
            InstructionServiceActions.showInstruction('instructions.move_corners');
          }
        }
        else if (!(model instanceof SpriteModel || model instanceof AnnotationModel)) {
          InstructionServiceActions.showInstruction('instructions.handle_interaction');
        }
      }
    }
    else if (model instanceof LoadedModel && model.isTransformable()) {
      InstructionServiceActions.showInstruction('instructions.handle_interaction');
    }
    else if (model instanceof ClassificationModel) {
      if (closeContextMenu) {
        ContextMenuStateServiceActions.openContextMenu(
          globals.LOWER_CONTEXT_MENU,
          globals.MEASURE_ACTIVATED
        );
      }
    }
    else if (model instanceof DistributedModel && model.areSpacingsEditable()) {
      if (closeContextMenu) {
        ContextMenuStateServiceActions.openContextMenu(
          globals.LOWER_CONTEXT_MENU,
          globals.DISTRIBUTE_ACTIVATED
        );
      }
    }
  }

  /**
   * Hook called after termination, overridden to remove merge event listener.
   * @override
   * @param { Element } element The DOM element.
   * @param { number } viewerId The viewer id.
   * @public
   */
  whenTerminated(element, viewerId) {
    super.whenTerminated(element, viewerId);
    if (this._mergeRightClickHandler) {
      element.removeEventListener('contextmenu', this._mergeRightClickHandler, false);
      this._mergeRightClickHandler = undefined;
    }
  }

  /**
   * Attempts to merge two line models if they share a single endpoint.
   * Shows feedback dialog with the result.
   * @param { scene.models.LineAndSpriteModel } modelA The first (selected) model.
   * @param { scene.models.LineAndSpriteModel } modelB The second (right-clicked) model.
   * @private
   */
  _attemptMerge(modelA, modelB) {
    const ptsA = modelA.getPoints();
    const ptsB = modelB.getPoints();

    if (ptsA.length === 0 || ptsB.length === 0) {
      this._showMergeFeedback(false);
      return;
    }

    const mergedPoints = this._computeMergedPoints(ptsA, ptsB);
    if (mergedPoints === undefined) {
      this._showMergeFeedback(false);
      return;
    }

    if (this._pointsEqual(mergedPoints[0], mergedPoints[mergedPoints.length - 1])) {
      return;
    }

    // Build merge command options from the first model's existing options
    const existingOptions = modelA.getOptions();
    const viewerId = this.getViewerId();
    const viewer = ViewerManager.instance().getSceneViewer(viewerId);
    const maxWidthFactor = viewer.getMaxWidthFactor();
    const fontFactor = viewer.getMaxWidthFontFactor();
    const transformable = (
      ModelManager.instance().getLinesMovable() ||
      ModelManager.instance().getLinesClonable()
    );

    const mergeCommand = new MergeLineAndSpriteModelsCommand({
      existingModelId: modelA.getId(),
      newModelId: modelB.getId(),
      nodeOptions: {
        transformable: transformable,
        pickable: true,
        transformType: existingOptions.nodeOptions !== undefined ? existingOptions.nodeOptions.transformType : undefined
      },
      modelOptions: {
        alwaysVisible: true,
        selectable: true,
        textColor: existingOptions.modelOptions !== undefined ? existingOptions.modelOptions.textColor : undefined,
        textContourColor: existingOptions.modelOptions !== undefined ? existingOptions.modelOptions.textContourColor : undefined
      },
      points: mergedPoints,
      drawPointsAndLines: true,
      lineColor: existingOptions.lineColor,
      measure: true,
      measureAccuracy: existingOptions.measureAccuracy,
      spriteWidth: existingOptions.spriteWidth !== undefined ? existingOptions.spriteWidth : 32 / maxWidthFactor,
      spriteHeight: existingOptions.spriteHeight !== undefined ? existingOptions.spriteHeight : 46 / maxWidthFactor,
      spriteAnchorPoint: existingOptions.spriteAnchorPoint,
      spriteImagePath: existingOptions.spriteImagePath !== undefined ? existingOptions.spriteImagePath : 'img/pin2_touch.png',
      implicitSelect: true,
      orthographicNormal: (viewer.isOrthographic() ? swea.util.negateVector(viewer.getCameraDirection()) : undefined),
      characterSize: existingOptions.characterSize !== undefined ? existingOptions.characterSize : 16 * fontFactor,
      showComponents: MeasureService.instance().areComponentsShown(),
      showAngle: MeasureService.instance().isAngleShown(),
      showTotalLength: MeasureService.instance().areTotalLengthsShown(),
      viewerId: viewerId
    });

    EditorStateServiceActions.addCommand(mergeCommand);
    this._showMergeFeedback(true);
  }

  /**
   * Computes the merged point array from two models' points, joining at the shared endpoint.
   * Returns undefined if no shared endpoint is found.
   * @param { number[][] } ptsA Points of the first model.
   * @param { number[][] } ptsB Points of the second model.
   * @returns { number[][] | undefined } The combined points, or undefined if no match.
   * @private
   */
  _computeMergedPoints(ptsA, ptsB) {
    const firstA = ptsA[0];
    const lastA = ptsA[ptsA.length - 1];
    const firstB = ptsB[0];
    const lastB = ptsB[ptsB.length - 1];

    if (this._pointsEqual(firstB, lastA)) {
      // B starts where A ends -> A + B[1:]
      return [...ptsA, ...ptsB.slice(1)];
    }
    if (this._pointsEqual(firstB, firstA)) {
      // B starts where A starts -> reverse A + B[1:]
      return [...ptsA.slice().reverse(), ...ptsB.slice(1)];
    }
    if (this._pointsEqual(lastB, firstA)) {
      // B ends where A starts -> B + A[1:]
      return [...ptsB, ...ptsA.slice(1)];
    }
    if (this._pointsEqual(lastB, lastA)) {
      // B ends where A ends -> B + reverse A[1:]
      return [...ptsB, ...ptsA.slice().reverse().slice(1)];
    }

    // No shared endpoint
    return undefined;
  }

  /**
   * Compares two 3D points for exact equality.
   * @param { number[] } a First point [x, y, z].
   * @param { number[] } b Second point [x, y, z].
   * @param { number } epsilon (optional) Tolerance for considering points equal. Default is 0.005.
   * @returns { boolean } True if equal.
   * @private
   */
  _pointsEqual(a, b, epsilon = 0.005) {
    return Math.abs(a[0] - b[0]) <= epsilon &&
      Math.abs(a[1] - b[1]) <= epsilon &&
      Math.abs(a[2] - b[2]) <= epsilon;
  }

  /**
   * Shows a feedback dialog indicating whether the merge succeeded or failed.
   * @param { boolean } success True if the merge was successful.
   * @private
   */
  _showMergeFeedback(success) {
    const title = success
      ? swea.i18n.t('components.merge_dialog.success_title')
      : swea.i18n.t('components.merge_dialog.fail_title');
    const text = success
      ? swea.i18n.t('components.merge_dialog.success_text')
      : swea.i18n.t('components.merge_dialog.fail_text');
    DialogServiceActions.open({
      title: title,
      text: text,
      alignment: DialogService.AlignmentType.CENTER,
      small: true,
      yesNo: false
    });
  }

  /**
   * Hides context menu on deselection.
   * @param { scene.models.Model } previousModel The previously selected model.
   * @public
   * @override
   */
  whenDeselected(previousModel) {
    super.whenDeselected(previousModel);
    if (previousModel === undefined) {
      return;
    }

    // Close menu only if there are no selected measurement or classification models
    // (only some of them can have been deselected)
    let closeMenu = true;
    const selectedModels = ModelManager.instance().getSelectedModels();
    const numSelectedModels = selectedModels.length;
    for (let i = 0; i < numSelectedModels; ++i) {
      const model = selectedModels[i];
      if (model instanceof ClassificationModel || (model instanceof PolygonBaseModel && model.usesMeasuring())) {
        closeMenu = false;
        break;
      }
    }
    if (closeMenu) {
      ContextMenuStateServiceActions.closeContextMenu(
        globals.LOWER_CONTEXT_MENU
      );
    }

    // Hide instruction
    InstructionServiceActions.hideInstruction();
  }

}
export default CustomSelectInteraction;
